import { SafeUrl } from '@angular/platform-browser';

import { Exclusion } from 'models';

export enum IntegrationType {
	ObsoleteGuesty = 1,
	Lavanda,
	RentalsUnited,
	Kigo,
	Lodgify,
	Bookster,
	BookingSync,
	Direct,
	Resharmonics,
	FantasticStay,
	Hostify,
	Hostaway,
	Kross,
	Zeevou,
	OwnerRez,
	Uplisting,
	BooksterButton,
	ThreeSixtyFiveVillas,
	TrackHs,
	Beds24,
	Tokeet,
	Lmpm,
	Smoobu,
	Hostfully,
	Myvr,
	Janiis,
	Avantio,
	Guesty,
}

export enum AuthenticationType {
	ApiKey = 1,
	UsernamePassword,
	ClientIdClientSecret,
	AuthorisationCode,
	Custom,
}

export class IntegrationDomain {
	id: number;
	identifier: string;
	integrationType: IntegrationTypeDomain;
	authenticationConfiguration: string;
	waitingForListingImport: boolean;
	createdByCutOffDate: Date;
	ignoreStaysOnFirstImport: boolean;
	excludeAirbnb: boolean;
	excludeDirect: boolean;
	exclusionValues: string;
	exclusion: Exclusion;
	isActive: boolean;
	authorisationCodeSetByCallback: boolean;
}

export class IntegrationListingViewModelContainer {
	integrationListingViewModels: IntegrationListingViewModel[];
	lastListingImport: ListingImportViewModel;
	lastListingImportWithChangeOrError: ListingImportViewModel;
	lastListingImportHasError: boolean;
}

export class IntegrationListingViewModel {
	id: number;
	listingIntegrationId: string;
	friendlyName: string;
	addressLine1: string;
	postcode: string;
	country: string;
	isActive: boolean;
	includeInSuperhog: boolean;
	summaryIfDifferentFromSuperhog: string;
	updateAllowed: boolean;
	changedInFrontEnd: boolean;
	reasonUpdateNotAllowed: string;
}

export class ListingImportViewModel {
	start: Date;
	end: Date;
	insertCount: number;
	updateCount: number;
	hasError: boolean;
	exception: string;
	lastImportMessage: string;
	insertSummaryDetails: ListingImportSummaryDetailViewModel[];
	updateSummaryDetails: ListingImportSummaryDetailViewModel[];
}

export class ListingImportSummaryDetailViewModel {
	listingIntegrationId: string;
	friendlyName: string;
}

export class IntegrationTypeDomain {
	id: string;
	name: string;
	displayName: string;
	isHidden: boolean;
	config: string;
	authenticationType: AuthenticationType;
	integrationTypeId: number;
	integrationTypeConfiguration: string;
	createdDateCutOffAllowed: boolean;
	logo: string;
	airBnbIsSupported: boolean;
	directIsSupported: boolean;
	reservationTypeIsSupported: boolean;
	vrboIsSupported: boolean;
}

export class IntegrationTypeConfiguration {
	value: string;
}

export class CreateIntegrationViewModel {
	integrationTypeId: number;
	authenticationType: AuthenticationType;
	configuration: string;
	createdByCutOffDate: Date;
	ignoreStaysOnFirstImport: boolean;
	excludeAirbnb: boolean;
	excludeDirect: boolean;
	exclusionValues: string;
	exclusion: Exclusion;
}

export class EditIntegrationViewModel {
	identifier: string;
	integrationTypeId: number;
	authenticationType: AuthenticationType;
	configuration: string;
	createdByCutOffDate: Date;
	ignoreStaysOnFirstImport: boolean;
	excludeAirbnb: boolean;
	excludeDirect: boolean;
	exclusionValues: string;
	exclusion: Exclusion;
}

export class NameValuePair {
	name: string;
	value: string;
}

export class ApiKeyAuthentication {
	apiKey: string;
}

export class IntegrationTokenAuthentication {
	integrationToken: string;
	clientId: string;
	clientSecret: string;
	inclusionSources: string;
}

export class UsernamePasswordAuthentication {
	username: string;
	password: string;
}

export class ClientIdClientSecretAuthentication {
	clientId: string;
	clientSecret: string;
}

export class AuthorisationCodeAuthentication {
	authenticationConfiguration: IntegrationAuthenticationConfiguration;
	authorisationCode: string;
	authorisationCodeUri: AuthorisationCodeUri;
}

export class AuthorisationCodeUri {
	value: SafeUrl;
}

export class IntegrationAuthenticationConfiguration {
	value: string;
}

export interface INameValuePairMap<T, U> {
	(model: T): U;
}

export class LmpmAuthentication {
	lmpmCompanyId: string;
}

export class BooksterButtonAuthentication {
	clientId: string;
}

export class ThreeSixFiveVillasAuthentication {
	ownerToken: string;
	apiKey: string;
	password: string;
}

export class TrackHsAuthentication {
	domainName: string;
	serverKey: string;
	serverSecret: string;
	exclusionValues: string;
}

export class HostfullyAuthentication {
	agencyUid: string;
}

export class IntegrationAuthenticationSaveContainer {
	authenticationConfiguration: NameValuePair[];
	createdByCutOffDate: Date;
	ignoreStaysOnFirstImport: boolean;
}

export class IntegrationAuthenticationContainer {
	integrationAuthentication: IntegrationAuthentication;
	createdDateCutOffAllowed: boolean;
	createdByCutOffDate: Date;
	ignoreStaysOnFirstImport: boolean;
}

export type IntegrationAuthentication =
	| ApiKeyAuthentication
	| UsernamePasswordAuthentication
	| ClientIdClientSecretAuthentication
	| AuthorisationCodeAuthentication
	| LmpmAuthentication
	| BooksterButtonAuthentication
	| ThreeSixFiveVillasAuthentication
	| TrackHsAuthentication
	| HostfullyAuthentication
	| IntegrationTokenAuthentication;
